/**
 * renderMode.js — на чём эта машина способна рисовать и что ей можно доверить.
 *
 * Появился из-за старого ноутбука Lenovo B570. Программа на нём упиралась в
 * стену: «WebGL2 Required», английская надпись и `return` — дальше не шло
 * ничего. При этом Cornerstone умеет рисовать срезы ПРОЦЕССОРОМ
 * (useCPURendering), и этот путь в init.js уже был написан. До него просто не
 * доходили: проверка в main.js обрывала запуск раньше.
 *
 * Модуль чистый — ни импортов, ни обращений к DOM. Всё, что ему нужно, ему
 * передают. Поэтому решение о режиме можно проверить тестом, не поднимая
 * браузер, и оно не расползётся по коду тремя разными «если».
 */

/** Признаки программного рендера в строке видеокарты. */
const SOFT = ['swiftshader', 'llvmpipe', 'software', 'basic render', 'microsoft basic'];

/**
 * @typedef {Object} RenderMode
 * @property {'full'|'software'|'cpu2d'} kind
 * @property {boolean} volume       — можно ли строить объём (MPR, 3D)
 * @property {boolean} cinematic    — можно ли кинематографический рендер
 * @property {boolean} cpuRendering — просить ли Cornerstone рисовать процессором
 * @property {string}  title        — заголовок для человека
 * @property {string}  why          — что именно случилось
 * @property {string[]} advice      — что человек может сделать
 */

/**
 * @param {{webgl2?: boolean, renderer?: string, webgpu?: boolean}} caps
 * @returns {RenderMode}
 */
export function decideRenderMode({ webgl2 = false, renderer = '', webgpu = false } = {}) {
  const r = String(renderer || '').toLowerCase();
  const soft = SOFT.some((s) => r.includes(s));

  // Нет WebGL2 вовсе. Объёма не будет: он строится через видеокарту, и
  // подделать это нечем. Но стопка срезов рисуется процессором — а это и есть
  // главное, ради чего программу открывают.
  if (!webgl2) {
    return {
      kind: 'cpu2d',
      volume: false,
      volumeSlow: false,
      cinematic: false,
      cpuRendering: true,
      title: 'Упрощённый режим: рисует процессор',
      why: 'Видеокарта этого компьютера не поддерживает WebGL2 — трёхмерные реформаты и объём построить не из чего. Снимки открываются и листаются, измерения и окно яркости работают.',
      advice: [
        'Если в ноутбуке есть отдельная видеокарта NVIDIA — поставьте для неё драйвер и назначьте браузеру «высокопроизводительный процессор NVIDIA»: тогда включится полный режим.',
        'Программный WebGL2 в Chrome: запускать ярлыком с ключами --use-angle=swiftshader --enable-unsafe-swiftshader. Реформаты заработают, но медленно.',
        'Проверить, что видит браузер, можно на странице chrome://gpu.',
      ],
    };
  }

  // WebGL2 есть, но считает процессор (SwiftShader и подобные). Реформаты
  // построятся, объёмный рендер — формально да, на деле кадры по секунде.
  // Честнее выключить его сразу, чем дать врачу ждать впустую.
  if (soft) {
    return {
      kind: 'software',
      volume: true,
      volumeSlow: true,
      cinematic: true,
      cpuRendering: false,
      // [10.4.5] История этой строчки — предупреждение самому себе.
      //
      // Сначала здесь стояло volume: true без предупреждения → четыре чёрных
      // окна и ни слова почему. Тогда я закрыл объём совсем (10.4.2) — и стало
      // хуже: у врача пропали аксиальная, корональная и сагиттальная проекции,
      // причём без объяснения, будто программа сломалась.
      //
      // Правильная граница проходит не там. Запрещать нужно то, что построить
      // НЕВОЗМОЖНО (нет WebGL2 вовсе — случай выше). А то, что просто МЕДЛЕННО,
      // запрещать нельзя: решать, стоит ли ждать полминуты ради реформата,
      // должен врач, а не я за него. Программа обязана предупредить — и всё.
      // [10.4.5] И ещё одна поправка, уже по факту с живой машины. На том же
      // Lenovo B570 версия с сайта показывает ВСЁ: и проекции, и срезы, и
      // объём, и 3D-пресет. Значит машина способна, а «программный» режим —
      // это диагноз не машине, а конкретной сборке Electron, которая не
      // сумела взять видеокарту. Отнимать у врача возможности на основании
      // такого диагноза нельзя. Здесь не осталось ни одного запрета: только
      // предупреждение о скорости.
      title: 'Похоже, считает процессор: возможны задержки',
      why: 'Браузер сообщает, что WebGL2 считает процессор, а не видеокарта. Ничего не отключено — всё доступно как обычно. Но реформаты и объём могут строиться заметно дольше, и окна в это время будут чёрными: это не поломка, а ожидание.',
      advice: [
        'Если та же программа на сайте работает быстро, а настольная — медленно, дело не в компьютере, а в настольной оболочке: пришлите строку «Видеокарта Electron» из окна диагностики.',
        'Если ждать некогда — работайте в одиночном окне: стопка срезов идёт с обычной скоростью.',
      ],
    };
  }

  return {
    kind: 'full',
    volume: true,
    volumeSlow: false,
    cinematic: true,
    cpuRendering: false,
    title: 'Полный режим',
    why: '',
    advice: [],
  };
}

/** Короткая строка для журнала и окна диагностики. */
export function describeRenderMode(mode) {
  if (!mode) return 'режим не определён';
  const parts = [mode.kind === 'full' ? 'полный' : mode.kind === 'software' ? 'программный WebGL2' : 'процессорный 2D'];
  parts.push(mode.volume ? 'объём: да' : 'объём: нет');
  parts.push(mode.cinematic ? 'кино-рендер: да' : 'кино-рендер: нет');
  return parts.join(', ');
}
